import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import { fetchFormData } from '../../../../Api/ApiMethods';
import { Calendar, DollarSign, FileText, Briefcase, Menu, Plus } from 'react-feather';
import ExpenseDataModal from '../../FinancialOperations/Expenses/ExpenseDataModal';
import PropTypes from 'prop-types';

const ExpenseCardList = ({
    accidentFileId,
    canEdit = true,
    customerId,
    callformId,
    callFormState, // Yeni prop: çağrı formu durumu
    initialCustomerContractRate,
    initialIsMiddlemanContractBased,
    initialMiddlemanContractRate,
    initialMiddlemanContractPrice
}) => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [modalMode, setModalMode] = useState('add');

    const expenseApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense';

    useEffect(() => {
        // Çağrı formu durumuna göre masrafları getir
        if (callFormState === 'Ödeme Bekleniyor' && callformId) {
            fetchExpenses();
        } else if (callFormState === 'Eksper Raporu Ödeme Bekleniyor' && accidentFileId) {
            fetchExpenses();
        } else if (accidentFileId) {
            // Fallback: accidentFileId varsa getir
            fetchExpenses();
        }
    }, [accidentFileId, callformId, callFormState]);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            let apiUrl = '';
            
            // Çağrı formu durumuna göre API endpoint'ini belirle
            if (callFormState === 'Ödeme Bekleniyor' && callformId) {
                // "Ödeme Bekleniyor" durumunda callform ID ile filtrele
                apiUrl = `${expenseApiUrl}?callForm=${callformId}`;
            } else if (callFormState === 'Eksper Raporu Ödeme Bekleniyor' && accidentFileId) {
                // "Eksper Raporu Ödeme Bekleniyor" durumunda accident file ID ile filtrele
                apiUrl = `${expenseApiUrl}?accidentFile=${accidentFileId}`;
            } else {
                // Fallback: accidentFileId varsa onu kullan
                if (!accidentFileId) return;
                apiUrl = `${expenseApiUrl}?accidentFile=${accidentFileId}`;
            }

            console.log('Fetching expenses from:', apiUrl);

            const response = await fetchFormData({
                apiEndpoint: apiUrl,
                page: 1,
                pageSize: 100,
            });

            let expensesData = [];

            if (response && Array.isArray(response.items)) {
                expensesData = response.items;
            } else if (response && Array.isArray(response.data?.items)) {
                expensesData = response.data.items;
            } else if (Array.isArray(response)) {
                expensesData = response;
            } else if (response && Array.isArray(response.data)) {
                expensesData = response.data;
            }

            console.log('Fetched expenses for state:', callFormState, expensesData);
            setExpenses(expensesData);
        } catch (error) {
            console.error('Error fetching expenses:', error);
            setExpenses([]);
        } finally {
            setLoading(false);
        }
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            // Reset selected expense when closing modal
            setTimeout(() => setSelectedExpense(null), 300);
        }
    };

    const handleEditExpense = (expense) => {
        setSelectedExpense(expense);
        setModalMode('modify');
        setModalOpen(true);
    };

    // const getStatusColor = (status) => {
    //     switch (status) {
    //         case 'Ödendi':
    //             return 'success';
    //         case 'Ödenmedi':
    //             return 'warning';
    //         default:
    //             return 'primary';
    //     }
    // };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return '-';
        }
    };

    const refreshExpenses = () => {
        // Refresh the expenses data without reloading the page
        fetchExpenses();
    };

    if (loading) {
        return <div className="text-center p-4">Masraflar yükleniyor...</div>;
    }

    return (
        <div className="expense-card-list">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">Masraflar</h4>
                {canEdit && (
                    <button
                        className="btn btn-outline-primary btn-sm d-flex align-items-center"
                        onClick={(e) => {
                            setSelectedExpense(null);
                            setModalMode('add');
                            setModalOpen(true);
                        }}
                        disabled={
                            (callFormState === 'Ödeme Bekleniyor' && !callformId) ||
                            (callFormState === 'Eksper Raporu Ödeme Bekleniyor' && !accidentFileId)
                        }
                        title={
                            callFormState === 'Ödeme Bekleniyor' && !callformId 
                                ? "Masraf eklemek için çağrı formunu kaydedin" 
                                : callFormState === 'Eksper Raporu Ödeme Bekleniyor' && !accidentFileId
                                ? "Masraf eklemek için kaza dosyası oluşturulmalı"
                                : ""
                        }
                        type="button"
                    >
                        <Plus size={18} className="me-1" />
                        Masraf Ekle
                    </button>
                )}
            </div>
            {expenses.length === 0 ? (
                <div className="text-center p-4">Bu çağrı formu için kayıtlı masraf bulunamadı.</div>
            ) : (
                <Row>
                    {expenses.map((expense) => (
                        <Col key={expense.id} md={6} lg={4} className="mb-3 d-flex">
                            <Card 
                                className="shadow-sm hoverable w-100 h-100" 
                                style={{ 
                                    cursor: canEdit ? 'pointer' : 'default', 
                                    fontSize: '0.95rem' 
                                }} 
                                onClick={canEdit ? () => handleEditExpense(expense) : undefined}
                            >
                                <CardHeader 
                                    className="d-flex justify-content-between align-items-center text-white py-2" 
                                    style={{ backgroundColor: '#59667a' }}
                                >
                                    <h6 className="mb-2 mt-2" style={{ wordBreak: 'break-word', maxWidth: '100%' }}>
                                        {expense.expenseType || 'Masraf'}
                                    </h6>
                                    {/* <div>
                                        <Badge
                                            color={getStatusColor(expense.paymentStatus)}
                                            className="px-2 py-1"
                                            style={{ fontSize: '13px', fontWeight: 'bold', borderRadius: '8px' }}
                                        >
                                            {expense.paymentStatus || 'Ödenmedi'}
                                        </Badge>
                                    </div> */}
                                </CardHeader>
                                <CardBody className="p-3">
                                    <div className="mb-2 d-flex align-items-center">
                                        <FileText size={18} className="me-2 text-info" />
                                        <div>
                                            <strong>Masraf No:</strong> {expense.expenseNumber || '-'}
                                        </div>
                                    </div>

                                    <div className="mb-2 d-flex align-items-center">
                                        <DollarSign size={18} className="me-2 text-success" />
                                        <div>
                                            <strong>Tutar:</strong> {expense.amount ? `${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(expense.amount)}` : '-'}
                                        </div>
                                    </div>

                                    <div className="mb-2 d-flex align-items-center">
                                        <Briefcase size={18} className="me-2 text-secondary" />
                                        <div>
                                            <strong>Banka:</strong> {expense.associatedBank && expense.associatedBank.bankName
                                                ? `${expense.associatedBank.bankName} - ${expense.associatedBank.branchName || ''}`
                                                : '-'}
                                        </div>
                                    </div>

                                    <div className="mb-2 d-flex align-items-center">
                                        <Calendar size={18} className="me-2 text-warning" />
                                        <div>
                                            <strong>Tarih:</strong> {formatDate(expense.date)}
                                        </div>
                                    </div>

                                    {expense.invoiceNumber && String(expense.invoiceNumber) !== '0' && (
                                        <div className="mb-2 d-flex align-items-center">
                                            <FileText size={18} className="me-2 text-secondary" />
                                            <div>
                                                <strong>Fatura No:</strong> {expense.invoiceNumber}
                                            </div>
                                        </div>
                                    )}

                                    {expense.description && (
                                        <div className="d-flex align-items-start mt-2">
                                            <Menu size={18} className="me-2 text-muted mt-1" />
                                            <div style={{ wordBreak: 'break-word', fontSize: '0.85rem' }} title={expense.description}>
                                                <strong>Açıklama:</strong> {expense.description.length > 50 ? `${expense.description.substring(0, 50)}...` : expense.description}
                                            </div>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Modal for editing expense */}
            {canEdit && (
                <ExpenseDataModal
                    apiEndpoint={expenseApiUrl}
                    isOpen={modalOpen}
                    toggle={toggleModal}
                    mode={modalMode}
                    selectedExpense={selectedExpense}
                    refreshData={refreshExpenses}
                    customerId={customerId}
                    autoAssignCustomer={true}
                    // Çağrı formu durumuna göre parametreleri belirle
                    accidentFile={callFormState === 'Eksper Raporu Ödeme Bekleniyor' && accidentFileId ? [accidentFileId] : []}
                    callformId={callFormState === 'Ödeme Bekleniyor' ? callformId : null}
                    initialCustomerContractRate={initialCustomerContractRate}
                    initialIsMiddlemanContractBased={initialIsMiddlemanContractBased}
                    initialMiddlemanContractRate={initialMiddlemanContractRate}
                    initialMiddlemanContractPrice={initialMiddlemanContractPrice}
                />
            )}
        </div>
    );
};

ExpenseCardList.propTypes = {
    accidentFileId: PropTypes.string,
    canEdit: PropTypes.bool,
    customerId: PropTypes.string,
    callformId: PropTypes.string,
    callFormState: PropTypes.string, // Yeni prop: çağrı formu durumu
    initialCustomerContractRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialIsMiddlemanContractBased: PropTypes.bool,
    initialMiddlemanContractRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialMiddlemanContractPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ExpenseCardList;
